<script setup>
import ProcessingCostsFilter from '@/components/processing-costs-page/ProcessingCostsFilter.vue'
import ProcessingCostsPdfExport from '@/components/processing-costs-page/ProcessingCostsPdfExport.vue'
import ProcessingCostsSummaryTable from '@/components/processing-costs-page/ProcessingCostsSummaryTable.vue'
import BaseLoadingSpinner from '@/components/base/BaseLoadingSpinner.vue'
import BaseAlert from '@/components/base/BaseAlert.vue'

import { computed, getCurrentInstance } from 'vue'
import { useStore } from '@/utils/VuexHelpers'
import { formatCurrency } from '@/helpers/stringHelpers'
import { PIG_INFO } from '@/constants/pagination'

const vm = getCurrentInstance().proxy
const store = useStore()

const filtersProcCost = computed(() => store.getters.filtersProcCost)
const procCostList = computed(() => store.getters.procCostList)
const loadingProcCost = computed(() => store.getters.loadingProcCost)
const missingCostTypeError = computed(() => store.getters.missingCostTypeError)

const headers = [
  {
    text: 'Description',
    align: 'left'
  },
  {
    text: 'Quantity',
    align: 'right'
  },
  {
    text: 'Unit',
    align: 'center'
  },
  {
    text: 'Unit Price ($)',
    align: 'right'
  },
  {
    text: 'Amount excl GST ($)',
    align: 'right'
  }
]

const totalSlaughter = computed(() => {
  const { SUK, SP, BP, XSMALL, SMALL, MEDIUM, LARGE, XLARGE, GRADING } =
    procCostList.value

  return (
    SUK.total +
    SP.total +
    BP.total +
    XSMALL.total +
    SMALL.total +
    MEDIUM.total +
    LARGE.total +
    XLARGE.total +
    GRADING.total
  )
})
const grandTotal = computed(() => {
  return totalSlaughter.value + procCostList.value.LEVY.total
})

const summaryRows = computed(() => {
  const rows = []
  if (!procCostList.value.GRADING) return rows

  const { SUK, SP, BP, XSMALL, SMALL, MEDIUM, LARGE, XLARGE, GRADING, LEVY } =
    procCostList.value

  // 1) Section Header “Slaughter”
  rows.push({ type: 'sectionHeader', label: 'Slaughter', colspan: 5 })

  // 2) For each category under “Slaughter,” only show if qty > 0
  const slaughterCats = [SUK, BP, SP, XSMALL, SMALL, MEDIUM, LARGE, XLARGE]
  slaughterCats.forEach((cat) => {
    if (cat.qty > 0) {
      rows.push({
        type: 'item',
        description: cat.config.description,
        qty: cat.qty,
        unit: cat.config.unit,
        unitPrice: cat.config.cost,
        amount: cat.total
      })
    }
  })

  // 3) Always include GRADING row (even if qty == 0)
  rows.push({
    type: 'item',
    description: GRADING.config.description,
    qty: GRADING.qty,
    unit: GRADING.config.unit,
    unitPrice: GRADING.config.cost,
    amount: GRADING.total
  })

  // 4) Sub-total row for Slaughter
  rows.push({
    type: 'subtotal',
    label: 'Totals',
    amount: totalSlaughter.value
  })

  // 5) Section Header “Government Levies”
  rows.push({ type: 'sectionHeader', label: 'Government Levies', colspan: 5 })

  // 6) LEVY item
  rows.push({
    type: 'item',
    description: LEVY.config.description,
    qty: LEVY.qty,
    unit: LEVY.config.unit,
    unitPrice: LEVY.config.cost,
    amount: LEVY.total
  })

  // 7) Grand Total row
  rows.push({
    type: 'grandTotal',
    label: `TOTAL (Excl. GST): ${formatCurrency(grandTotal.value, 2)}`
  })

  return rows
})

// ON CREATED
store.commit('SET_USING_VUETIFY', true)
store.commit('SET_ACTIVE', PIG_INFO)
store.dispatch('fetchProcCost', { vm })
</script>

<template>
  <v-app>
    <BaseAlert />

    <v-container fluid>
      <div class="wrapper">
        <ProcessingCostsFilter />

        <div class="content-page mt-0">
          <v-row no-gutters class="my-2 align-center">
            <div class="page-title-box d-flex">
              <h4 class="page-title">Processing Costs</h4>
            </div>
            <ProcessingCostsPdfExport
              :rows="summaryRows"
              :selected-week="filtersProcCost.week.value"
              :table-headers="headers"
            />
          </v-row>

          <ProcessingCostsSummaryTable
            v-if="loadingProcCost !== 'list' && missingCostTypeError === ''"
            :rows="summaryRows"
            :selected-week="filtersProcCost.week.value"
            :table-headers="headers"
          />

          <BaseLoadingSpinner
            v-else-if="loadingProcCost === 'list'"
            :text="'Processing Costs'"
          />

          <v-card v-else>
            <v-card-text class="text-danger">
              <div class="mb-3">{{ missingCostTypeError }}</div>
              <v-btn small color="red" :to="{ name: 'systemSettings' }"
                >Create new Cost</v-btn
              >
            </v-card-text>
          </v-card>
        </div>
      </div>
    </v-container>
  </v-app>
</template>

<style>
.v-application .keep-default-border > .grey.lighten-5 {
  border-color: rgba(0, 0, 0, 0.1) !important;
}
</style>

<style scoped>
p {
  margin-bottom: 0;
  line-height: 1.1;
}
</style>
